/**
 * หน้าจอตามสอบ Lot — สืบย้อนกลับและสืบไปข้างหน้าของ lot หนึ่งใบ
 *
 * **อ่านอย่างเดียวทั้งหน้าจอ** มันเรียกเพียงสองเส้นทางคือ
 * `GET /v1/lots/{lot_id}/genealogy/backward` และ `/forward` (D-073) และไม่เรียก
 * endpoint อื่นเลย จึงไม่มีการ์ดสิทธิ์ตัวที่สองข้างใน ต่างจาก `GoodsReceiptScreen`
 * ที่ต้องกันฟอร์มรับของเข้าด้วย CREATE — ที่นี่ไม่มีปุ่มใดให้กัน ด่านเดียวของมันคือ
 * `INVENTORY` + VIEW ที่ระดับ route ซึ่งผ่านมาแล้วเมื่อ component นี้ถูกเรนเดอร์
 * (กติกาเดียวกับ `system/RbacAuditLogScreen`)
 *
 * **ทั้งสองทิศทางแสดงพร้อมกันเสมอ** ไม่ใช่แท็บที่ต้องสลับ เพราะสองคำถามที่หน้าจอนี้
 * มีอยู่เพื่อตอบเกิดขึ้นพร้อมกันเสมอในเหตุการณ์จริงหนึ่งครั้ง: ของเสียถูกพบที่ปลายทาง
 * (ต้องย้อนกลับไปหาวัตถุดิบ) แล้ววัตถุดิบกองนั้นก็ต้องถูกตามไปข้างหน้าทันทีว่าไปอยู่
 * ในสินค้าตัวใดอีกบ้าง — หน้าจอที่ให้ดูได้ทีละทิศทางบังคับให้ผู้ใช้จำครึ่งหนึ่งไว้ในหัว
 * ระหว่างสลับแท็บ ซึ่งเป็นตอนที่ของตกหล่นจากการเรียกคืน
 *
 * ⚠️ single-level หนึ่ง hop — ดู `LotGenealogyPanel`
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { LotGenealogyPanel } from './LotGenealogyPanel';
import {
  LOT_ID_PARAM,
  qualityStatusLabel,
  qualityStatusTone,
  resolveLotTraceabilityView,
} from './lotTraceabilityView';
import { useLotGenealogy } from './useLotGenealogy';
import { InfoHint } from '../components/InfoHint';
import { StatusBadge } from '../StatusBadge';
import {
  GENEALOGY_BACKWARD,
  GENEALOGY_FORWARD,
  type LotGenealogyLot,
} from '../../lib/lotTraceability/types';

export interface LotTraceabilityScreenProps {
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `apiFetch` ผ่าน client */
  readonly fetchImpl?: typeof fetch;
}

interface LotGenealogyViewProps {
  readonly lotId: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

/**
 * สองทิศทางของ lot ที่ถูกระบุแล้ว
 *
 * แยกจาก `LotTraceabilityScreen` เพราะ hook ที่ยิงคำขอต้องไม่ถูกเรียกเลยตอนที่ยัง
 * ไม่มี lot ให้ถาม — และเพราะ component นี้ถูก `key` ด้วย `lotId` การเดินไปยัง lot
 * ถัดไปจึงเริ่มจากสถานะเปล่าจริง ๆ ไม่ใช่ตารางของ lot ก่อนหน้าที่ค้างอยู่ระหว่างโหลด
 */
function LotGenealogyView({ lotId, apiBaseUrl, fetchImpl }: LotGenealogyViewProps) {
  const backward = useLotGenealogy({
    lotId,
    direction: GENEALOGY_BACKWARD,
    apiBaseUrl,
    fetchImpl,
  });
  const forward = useLotGenealogy({
    lotId,
    direction: GENEALOGY_FORWARD,
    apiBaseUrl,
    fetchImpl,
  });

  /**
   * lot ที่ถูกถาม — อ่านจากทิศทางไหนก็ได้ที่สำเร็จแล้ว
   *
   * ทั้งสองคำตอบบรรจุ `lot` ก้อนเดียวกัน (contract) และเราไม่ยิงคำขอที่สามเพื่อ
   * ถามซ้ำในสิ่งที่มากับคำตอบอยู่แล้ว — เหตุผลเดียวกับที่ `GoodsReceiptDetailView`
   * ไม่ยิงคำขอที่สองเพื่ออ่าน `quality_status` ของ lot
   */
  const subject: LotGenealogyLot | null =
    backward.state.status === 'ready'
      ? backward.state.lot
      : forward.state.status === 'ready'
        ? forward.state.lot
        : null;

  /**
   * ไม่พบทั้งสองทิศทาง = ไม่พบ lot — พูดครั้งเดียวให้ชัด
   *
   * `404` ของ endpoint นี้เป็นคำตอบเกี่ยวกับ **lot** ไม่ใช่เกี่ยวกับทิศทาง การ
   * แสดงกล่องเดียวกันสองใบข้าง ๆ กันอ่านเหมือนระบบพังสองที่ ทั้งที่สิ่งที่เกิดขึ้นคือ
   * เลข lot นี้ไม่มีอยู่ในมุมมองของผู้ใช้คนนี้
   */
  const lotNotFound =
    backward.state.status === 'not-found' && forward.state.status === 'not-found';

  if (lotNotFound) {
    return (
      <div className="cap-notice" data-testid="lot-traceability-not-found" role="status">
        <StatusBadge tone="danger">ไม่พบ lot นี้</StatusBadge>
        <h2 className="cap-notice__title">ไม่พบ lot {lotId} ในมุมมองของคุณ</h2>
        <p className="cap-notice__text">
          เซิร์ฟเวอร์ตอบ 404 ทั้งสองทิศทาง — ระบบไม่ยืนยันว่า lot นี้ไม่มีอยู่จริง
          หรือมีอยู่แต่อยู่ในโรงงานที่คุณไม่มีสิทธิ์เห็น สองอย่างนี้ตอบเหมือนกันโดยตั้งใจ (D-004)
        </p>
        <p className="cap-notice__note">
          ตรวจว่าเลขที่กรอกตรงกับป้ายหรือไม่ ถ้าตรงและยังไม่พบ ให้ถามผู้ดูแลระบบว่า lot นี้อยู่ในโรงงานใด
        </p>
      </div>
    );
  }

  return (
    <>
      <section className="cap-meta" aria-label="lot ที่กำลังตามสอบ">
        <div className="cap-meta__item">
          <div className="cap-meta__label">lot ที่กำลังดู</div>
          <div className="cap-meta__value cap-table__code" data-testid="lot-traceability-lot-id">
            {lotId}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">สินค้า</div>
          <div className="cap-meta__value cap-table__code" data-testid="lot-traceability-product">
            {subject?.product_code ?? '—'}
          </div>
        </div>
        <div className="cap-meta__item">
          <div className="cap-meta__label">สถานะคุณภาพ</div>
          <div className="cap-meta__value" data-testid="lot-traceability-quality">
            {subject === null ? (
              '—'
            ) : (
              <StatusBadge tone={qualityStatusTone(subject.quality_status)}>
                {qualityStatusLabel(subject.quality_status)}
              </StatusBadge>
            )}
          </div>
        </div>
      </section>

      <LotGenealogyPanel
        direction={GENEALOGY_BACKWARD}
        lotId={lotId}
        state={backward.state}
        onLoadMore={backward.loadMore}
        onRetry={backward.retry}
      />
      <LotGenealogyPanel
        direction={GENEALOGY_FORWARD}
        lotId={lotId}
        state={forward.state}
        onLoadMore={forward.loadMore}
        onRetry={forward.retry}
      />
    </>
  );
}

export function LotTraceabilityScreen({ apiBaseUrl, fetchImpl }: LotTraceabilityScreenProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = resolveLotTraceabilityView(searchParams);
  const lotId = view.kind === 'lot' ? view.lotId : '';

  const [draft, setDraft] = useState(lotId);

  // ตามสิ่งที่ URL บอกเสมอ — ผู้ใช้เดินมาถึง lot นี้ได้ด้วยการคลิก edge หรือกดปุ่ม
  // ย้อนกลับของเบราว์เซอร์ ไม่ใช่ด้วยการพิมพ์เท่านั้น ช่องค้นหาที่ค้างเลขเก่าไว้จะ
  // ขัดกับตารางที่อยู่ข้างใต้มัน
  useEffect(() => {
    setDraft(lotId);
  }, [lotId]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = draft.trim();
    // ค่าว่าง = กลับไปสถานะ "ยังไม่ได้เลือก lot" ไม่ใช่การยิงคำขอที่ไม่มีประธาน
    setSearchParams(next === '' ? {} : { [LOT_ID_PARAM]: next });
  };

  return (
    <div className="cap-page" data-testid="lot-traceability">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ตามสอบ Lot{' '}
            <InfoHint
              label="เกี่ยวกับหน้าตามสอบ Lot"
              text={
                <>
                  อ่านอย่างเดียว — แสดงความสัมพันธ์ <strong>ตรงหนึ่งก้าว</strong> ของ lot ที่ระบุ
                  ทั้งขาเข้าและขาออก การไล่ต่อทำได้โดยคลิก lot ในตาราง ซึ่งจะเปิดหน้าจอนี้ให้ lot นั้น
                  (Phase 1 เป็น single-level ตาม D-073)
                </>
              }
            />
          </h1>
        </div>
      </div>

      <form className="cap-filters" onSubmit={submit} aria-label="ค้นหา lot ที่ต้องการตามสอบ">
        <label className="cap-field">
          <span className="cap-field__label">เลข lot (lot_id)</span>
          <input
            className="cap-input"
            type="search"
            name={LOT_ID_PARAM}
            data-testid="lot-traceability-lot-input"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            placeholder="เช่น c4f1b0a2-7e3d-4c58-9a10-2b6d8e0f1a30"
          />
          <InfoHint
            label="เกี่ยวกับเลข lot"
            text="เลขนี้คือ lot id ที่อยู่บนป้ายของกองวัตถุดิบหรือบนใบรับเข้า — lot ที่อยู่นอกโรงงานที่คุณมีสิทธิ์เห็น ตอบเหมือน lot ที่ไม่มีอยู่จริง (D-004)"
          />
        </label>
        <button type="submit" className="cap-button cap-button--primary" data-testid="lot-traceability-submit">
          ตามสอบ lot นี้
        </button>
      </form>

      {view.kind === 'prompt' ? (
        <div className="cap-notice" data-testid="lot-traceability-prompt" role="status">
          <StatusBadge tone="info">ยังไม่ได้เลือก lot</StatusBadge>
          <h2 className="cap-notice__title">กรอกเลข lot เพื่อเริ่มตามสอบ</h2>
          <p className="cap-notice__text">
            หน้าจอนี้ตอบคำถามเกี่ยวกับ lot ใด lot หนึ่งเสมอ จึงยังไม่ยิงคำขอใดจนกว่าจะมี lot ให้ถาม
          </p>
        </div>
      ) : (
        <LotGenealogyView key={lotId} lotId={lotId} apiBaseUrl={apiBaseUrl} fetchImpl={fetchImpl} />
      )}
    </div>
  );
}
